import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { readPersistedWorkspace } from "../../../utils/workspacePersistence";
import {
  exchangeLinkedInToken,
  exchangeLinkedInBusinessToken,
  saveLinkedInOrganization,
  LinkedInOrganizationAcl,
} from "../../../services/socialTokenService";
import { getAllCustomers } from "../../../services/customerService";
import { Customer } from "../../../types/customer";
import { debug } from "../../../utils/debugLogger";
import { validateOAuthState } from "../../../utils/oauthState";

const resolveToken = (authIdToken: string): string => {
  if (authIdToken) return authIdToken;
  const pending = localStorage.getItem("linkedin_pending_token");
  if (pending) return pending;
  const stored = localStorage.getItem("idToken");
  return stored ?? "";
};

function extractOrgName(acl: LinkedInOrganizationAcl): string {
  return (
    acl["organization~"]?.localizedName ||
    acl["organization~"]?.vanityName ||
    acl.organization
  );
}

function extractOrgLogo(acl: LinkedInOrganizationAcl): string {
  const elements = acl["organization~"]?.logoV2?.["original~"]?.elements;
  return elements?.[0]?.identifiers?.[0]?.identifier ?? "";
}

type Step = "select-customer" | "select-page" | "done";
type Mode = "personal" | "business";

const LinkedInCallback = ({ mode = "personal" }: { mode?: Mode }) => {
  const navigate = useNavigate();
  const { idToken: authIdToken, isLoading, isAuthenticated } = useAuth();
  const idToken = resolveToken(authIdToken);
  // The OAuth redirect to LinkedIn and back is a full page navigation, so any
  // in-memory state resets — this route also sits outside MainLayout, where
  // CustomerWorkspaceProvider lives, so useCustomerWorkspace() isn't available
  // here. Read the persisted selection directly instead (same localStorage key
  // that context reads from) so this defaults to the business you were
  // already on, rather than requiring you to reselect it from a long list —
  // and risk silently connecting it to the wrong one.
  const [code, setCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(
    () => readPersistedWorkspace().id ?? ""
  );
  const [customersList, setCustomersList] = useState<Customer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // After token exchange
  const [step, setStep] = useState<Step>("select-customer");
  const [profileId, setProfileId] = useState<string>("");
  const [profileName, setProfileName] = useState<string>("");
  const [profilePicture, setProfilePicture] = useState<string>("");
  const [organizations, setOrganizations] = useState<LinkedInOrganizationAcl[]>([]);
  // Personal flow defaults to the personal-only sentinel; business flow requires an org pick.
  const [selectedOrgUrn, setSelectedOrgUrn] = useState<string>(
    mode === "business" ? "" : "__personal__"
  );

  const callbackPath =
    mode === "business" ? "/connect/linkedin/business/callback" : "/connect/linkedin/callback";

  useEffect(() => {
    if (isLoading) return;
    const urlParams = new URLSearchParams(window.location.search);
    const urlCode = urlParams.get("code");
    const errorParam = urlParams.get("error");
    if (errorParam) {
      setError(`LinkedIn authorization failed: ${errorParam}`);
      return;
    }
    // CSRF: the state returned must match the one we stored before redirecting.
    const stateKey = mode === "business" ? "linkedin_business" : "linkedin";
    if (!validateOAuthState(stateKey, urlParams.get("state"))) {
      setError("Security check failed (invalid OAuth state). Please try connecting again.");
      return;
    }
    if (!urlCode) {
      setError("No authorization code received. Please try connecting again.");
      return;
    }
    setCode(urlCode);
  }, [isLoading, mode]);

  useEffect(() => {
    if (!idToken || !code) return;
    const fetchCustomers = async () => {
      setCustomersLoading(true);
      try {
        const customers = await getAllCustomers(idToken);
        setCustomersList(customers);
      } catch (err) {
        debug.error("Error fetching customers:", err);
      } finally {
        setCustomersLoading(false);
      }
    };
    fetchCustomers();
  }, [idToken, code]);

  const handleExchangeToken = async () => {
    if (!code || !selectedCustomerId) {
      setError("Please select a business profile to continue.");
      return;
    }
    const currentToken = resolveToken(authIdToken);
    if (!currentToken) {
      setError("Please log in first, then connect LinkedIn.");
      return;
    }
    const redirectUri = `${window.location.origin}${callbackPath}`;
    setError(null);
    setIsSaving(true);
    try {
      if (mode === "business") {
        // Business (Community Management) flow — orgs only, no personal profile.
        const response = await exchangeLinkedInBusinessToken(
          code,
          selectedCustomerId,
          currentToken,
          redirectUri
        );
        localStorage.removeItem("linkedin_pending_token");
        const orgs = response.organizations ?? [];
        setOrganizations(orgs);
        if (orgs.length > 0) {
          setStep("select-page");
        } else {
          setError("No LinkedIn Pages found that you administer with this account.");
        }
      } else {
        // Personal flow — login + personal feed posting. No organizations here.
        const response = await exchangeLinkedInToken(
          code,
          selectedCustomerId,
          currentToken,
          redirectUri
        );
        if (!response?.profile) throw new Error("Invalid response from LinkedIn token exchange");

        setProfileId(response.profile_id);
        setProfileName(response.profile.name);
        setProfilePicture(response.profile.picture ?? "");
        localStorage.removeItem("linkedin_pending_token");
        setStep("done");
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to connect LinkedIn. Please try again.";
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePage = async () => {
    if (selectedOrgUrn === "__personal__") {
      setStep("done");
      return;
    }
    const currentToken = resolveToken(authIdToken);
    const acl = organizations.find((o) => o.organization === selectedOrgUrn);
    setIsSaving(true);
    setError(null);
    try {
      await saveLinkedInOrganization(
        selectedCustomerId,
        currentToken,
        selectedOrgUrn,
        profileId,
        acl ? extractOrgName(acl) : undefined,
        acl ? extractOrgLogo(acl) : undefined
      );
      setStep("done");
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to save LinkedIn Page. Please try again.";
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const goToConnect = () => navigate("/social-posts?tab=connect");

  if (!isLoading && !isAuthenticated && !localStorage.getItem("linkedin_pending_token")) {
    sessionStorage.setItem("linkedin_auth_redirect", "true");
    navigate("/login", { state: { returnUrl: "/social-posts?tab=connect" } });
    return null;
  }

  if (error && !code) {
    return (
      <div className="flex flex-col min-h-screen p-4">
        <BackButton onClick={goToConnect} />
        <div className="flex flex-col items-center justify-center flex-1">
          <div className="w-full max-w-4xl bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
            <p className="text-red-600 mb-4">{error}</p>
            <button type="button" onClick={goToConnect} className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover">
              Return to Connect Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen p-4">
        <BackButton onClick={goToConnect} />
        <div className="flex flex-col items-center justify-center flex-1">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen p-4">
      <BackButton onClick={goToConnect} />
      <div className="flex flex-col items-center w-full flex-1">
        <div className="w-full max-w-4xl bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
          <header className="mb-8 pb-8 border-b border-gray-100">
            <h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-1">
              {mode === "business" ? "Connect LinkedIn Business Page" : "Connect LinkedIn"}
            </h1>
            <p className="text-sm text-gray-500">
              {mode === "business"
                ? "Connect a LinkedIn Company Page you administer to a business profile."
                : "Connect your LinkedIn account to a business profile."}
            </p>
          </header>

          {step === "done" ? (
            <DoneStep
              profileName={profileName}
              profilePicture={profilePicture}
              selectedOrgUrn={selectedOrgUrn}
              organizations={organizations}
              onDone={goToConnect}
            />
          ) : step === "select-page" ? (
            <SelectPageStep
              mode={mode}
              organizations={organizations}
              selectedOrgUrn={selectedOrgUrn}
              onSelectOrg={setSelectedOrgUrn}
              onSave={handleSavePage}
              isSaving={isSaving}
              error={error}
            />
          ) : code ? (
            <SelectCustomerStep
              customersList={customersList}
              customersLoading={customersLoading}
              selectedCustomerId={selectedCustomerId}
              onSelectCustomer={setSelectedCustomerId}
              onConnect={handleExchangeToken}
              isSaving={isSaving}
              error={error}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const BackButton = ({ onClick }: { onClick: () => void }) => (
  <div className="w-full flex justify-start pl-4 mb-2">
    <button type="button" onClick={onClick} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium">
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
      </svg>
      Back
    </button>
  </div>
);

const SelectCustomerStep = ({
  customersList, customersLoading, selectedCustomerId,
  onSelectCustomer, onConnect, isSaving, error,
}: {
  customersList: Customer[];
  customersLoading: boolean;
  selectedCustomerId: string;
  onSelectCustomer: (id: string) => void;
  onConnect: () => void;
  isSaving: boolean;
  error: string | null;
}) => (
  <>
    <div className="mb-4">
      <h2 className="text-sm font-semibold text-gray-700">LinkedIn account authorized</h2>
      <p className="text-xs text-gray-500 mt-0.5">Select a business profile to connect this LinkedIn account to.</p>
    </div>
    {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
    <div className="max-w-xl mb-6">
      <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col shadow-sm">
        <div className="mb-4">
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
            Assign Profile
          </label>
          <select
            value={selectedCustomerId}
            onChange={(e) => onSelectCustomer(e.target.value)}
            disabled={customersLoading}
            className="block w-full rounded-lg border border-gray-200 text-xs h-9 px-3 focus:ring-brand-primary focus:border-brand-primary"
          >
            <option value="">-- Select a Business Profile --</option>
            {customersList.map((c) => (
              <option key={c.customer_id} value={c.customer_id}>
                {c.business_name}{c.city && c.state ? ` - ${c.city}, ${c.state}` : ""}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={onConnect}
          disabled={!selectedCustomerId || isSaving}
          className="w-full py-2 text-xs text-white bg-brand-primary rounded-lg font-semibold hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
        >
          {isSaving ? (
            <span className="flex items-center justify-center gap-1">
              <span className="animate-spin inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
              Connecting...
            </span>
          ) : "Connect"}
        </button>
      </div>
    </div>
  </>
);

const SelectPageStep = ({
  mode, organizations, selectedOrgUrn, onSelectOrg, onSave, isSaving, error,
}: {
  mode: Mode;
  organizations: LinkedInOrganizationAcl[];
  selectedOrgUrn: string;
  onSelectOrg: (urn: string) => void;
  onSave: () => void;
  isSaving: boolean;
  error: string | null;
}) => (
  <>
    <div className="mb-4">
      <h2 className="text-sm font-semibold text-gray-700">Select a LinkedIn Page</h2>
      <p className="text-xs text-gray-500 mt-0.5">
        {mode === "business"
          ? "Choose a Company Page you administer to connect."
          : "Choose a Business Page to publish to, or post as your personal profile only."}
      </p>
    </div>
    {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
    <div className="max-w-xl mb-6 space-y-2">
      {/* Personal-only option — personal flow only (business flow requires an org). */}
      {mode !== "business" && (
        <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${selectedOrgUrn === "__personal__" ? "border-brand-primary bg-brand-primary/10" : "border-gray-200 hover:border-gray-300"}`}>
          <input
            type="radio"
            name="org"
            value="__personal__"
            checked={selectedOrgUrn === "__personal__"}
            onChange={() => onSelectOrg("__personal__")}
            className="accent-brand-primary"
          />
          <div>
            <p className="text-sm font-medium text-gray-900">Personal profile only</p>
            <p className="text-xs text-gray-500">Posts will be published from your personal LinkedIn account.</p>
          </div>
        </label>
      )}

      {organizations.map((acl) => {
        const name = extractOrgName(acl);
        const logo = extractOrgLogo(acl);
        return (
          <label
            key={acl.organization}
            className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${selectedOrgUrn === acl.organization ? "border-brand-primary bg-brand-primary/10" : "border-gray-200 hover:border-gray-300"}`}
          >
            <input
              type="radio"
              name="org"
              value={acl.organization}
              checked={selectedOrgUrn === acl.organization}
              onChange={() => onSelectOrg(acl.organization)}
              className="accent-brand-primary"
            />
            {logo && <img src={logo} alt={name} className="w-8 h-8 rounded object-cover flex-shrink-0" />}
            <div>
              <p className="text-sm font-medium text-gray-900">{name}</p>
              <p className="text-xs text-gray-400">{acl.organization}</p>
            </div>
          </label>
        );
      })}
    </div>
    <div className="flex justify-end">
      <button
        type="button"
        onClick={onSave}
        disabled={isSaving || !selectedOrgUrn}
        className="px-5 py-2 rounded-lg bg-brand-primary text-white text-sm font-semibold hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all"
      >
        {isSaving ? (
          <span className="flex items-center gap-1">
            <span className="animate-spin inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
            Saving...
          </span>
        ) : "Save"}
      </button>
    </div>
  </>
);

const DoneStep = ({
  profileName, profilePicture, selectedOrgUrn, organizations, onDone,
}: {
  profileName: string;
  profilePicture: string;
  selectedOrgUrn: string;
  organizations: LinkedInOrganizationAcl[];
  onDone: () => void;
}) => {
  const selectedOrg = organizations.find((o) => o.organization === selectedOrgUrn);
  const displayName = selectedOrg ? extractOrgName(selectedOrg) : profileName;
  const displayPicture = selectedOrg ? extractOrgLogo(selectedOrg) : profilePicture;
  const subtitle = selectedOrg ? "Business Page connected" : "Personal profile connected";

  return (
    <>
      <div className="mb-6 flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
        {displayPicture && (
          <img src={displayPicture} alt={displayName} className="w-14 h-14 rounded-full object-cover" />
        )}
        <div>
          <p className="font-semibold text-gray-900">{displayName}</p>
          <p className="text-sm text-green-700 font-medium">{subtitle}</p>
        </div>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onDone}
          className="px-4 py-2 rounded-lg bg-gray-800 text-white font-medium hover:bg-gray-900"
        >
          Done
        </button>
      </div>
    </>
  );
};

export default LinkedInCallback;
